/**
 * MessageList — virtualised scrollable message list.
 *
 * Uses ``react-virtuoso`` so only the rows currently in the viewport are
 * mounted, keeping scroll smooth at any history depth. Wires three
 * Virtuoso callbacks into the store:
 *
 *   * ``startReached``     → ``loadOlderMessages`` (upward pagination)
 *   * ``atBottomStateChange`` → ``setAtBottom``       (drives auto-prune)
 *   * ``followOutput``     → keeps the view pinned at the bottom while
 *                             streaming, but yields the moment the user
 *                             scrolls up.
 *
 * Consecutive tool_use messages are still grouped into a collapsible
 * ToolCallGroup — that logic lives in ``renderItems`` below.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso, type VirtuosoHandle, type FlatIndexLocationWithAlign } from "react-virtuoso";
import { useChatStore, useRunningSubagents } from "@/stores/chatStore";
import type { Message, ToolCallState } from "@/api/types";
import { parseUTC } from "@/api/types";
import { ArtifactCard } from "./ArtifactCard";
import { GenUIFrame } from "./GenUIFrame";
import { MessageBubble } from "./MessageBubble";
import { MessageErrorBoundary } from "./MessageErrorBoundary";
import { ToolCallGroup } from "./ToolCallGroup";
import { ApprovalBanner } from "./ApprovalBanner";
import { UserQuestionWidget } from "./UserQuestionWidget";
import { EvaluatorBlock } from "./EvaluatorBlock";
import { StreamingBubble } from "./StreamingBubble";
import { StreamingIndicator } from "./StreamingIndicator";
import { ThinkingPanel } from "./ThinkingPanel";
import {
  buildToolResultMap,
  resolveToolCall as resolveToolCallShared,
} from "./transcriptShared";

const EMPTY_QUEUE: Array<{ id: string; text: string }> = [];

/** A renderable item: message, tool group, artifact, genui widget, or
 *  the inline "Unread" separator. */
type RenderItem =
  | { kind: "message"; msg: Message }
  | { kind: "toolGroup"; id: string; ts: number; toolCalls: ToolCallState[] }
  | { kind: "artifact"; id: string; ts: number; artifactId: string; filename: string; fileType: string }
  | { kind: "genui"; id: string; ts: number; genuiId: string; html: string; title: string; filePath: string }
  | { kind: "unreadDivider"; id: "unread-divider" }
  | { kind: "dateSeparator"; id: string; ts: number };

/** Basename of a POSIX or Windows path — used to match a widget's backing
 *  file against the registered artifacts so the CTA can show "Saved". */
function basenameOf(p: string): string {
  const lastSep = Math.max(p.lastIndexOf("/"), p.lastIndexOf("\\"));
  return lastSep >= 0 ? p.slice(lastSep + 1) : p;
}

/** Stable key for a render item. A message/artifact/genui/divider keys off a
 *  fixed message id; a tool group keys off the id of its first member, which
 *  *can* change if older tool_use rows merge into it across a page boundary.
 *  Shared by ``computeItemKey`` (Virtuoso row reuse) and the ``firstItemIndex``
 *  anchor below so both agree on identity. */
function keyOfItem(item: RenderItem): string {
  if (item.kind === "message") return `msg:${item.msg.id}`;
  if (item.kind === "toolGroup") return `tg:${item.id}`;
  return `${item.kind}:${item.id}`;
}

/** Baseline for ``firstItemIndex``. Large and constant so that, as upward
 *  pagination prepends pages, the index assigned to the first row keeps
 *  decreasing while staying comfortably positive (Virtuoso requires a positive
 *  ``firstItemIndex``). 1e6 leaves room for ~1M prepended items — far beyond
 *  any real conversation. */
const FIRST_ITEM_INDEX_BASE = 1_000_000;

/** Distance from the bottom (px) within which the view still counts as
 *  "following": content/footer growth keeps it pinned and the scroll-to-bottom
 *  CTA stays hidden. It also doubles as the buffer a *deliberate* upward scroll
 *  must clear before stick releases — see ``handleUserScroll``. Sized so a small
 *  nudge, or a tall streamed block landing a frame ahead of the re-pin, never
 *  surfaces the CTA or drops auto-follow. */
const STICK_THRESHOLD = 120;

/** Hysteresis band for the "reached the top of this chat" marker. Revealing
 *  the marker grows the header and nudges ``scrollTop`` down by the marker's
 *  own height; a single threshold would then bounce the test back across it
 *  and flicker. Showing below ``SHOW`` and hiding only above ``HIDE`` — with a
 *  gap wider than the marker (~48px) — makes the reveal stable in both
 *  directions. */
const TOP_REACH_SHOW = 24;
const TOP_REACH_HIDE = 96;

/** Horizontal separator marking the boundary between messages the user
 *  saw before opening the chat and the assistant turns that arrived
 *  while they were away. Mirrors the pattern in Slack / Gmail. */
function UnreadDivider() {
  return (
    <div
      className="flex items-center gap-3 select-none"
      role="separator"
      aria-label="Unread messages"
    >
      <div className="flex-1 h-px bg-jove-60" />
      <span className="text-[11px] font-semibold uppercase tracking-wider text-jove">
        Unread
      </span>
      <div className="flex-1 h-px bg-jove-60" />
    </div>
  );
}

/** Local calendar-day key (``year-month-date``) for bucketing messages into
 *  days. Uses the viewer's local timezone so a day boundary lands where their
 *  own clock says midnight — the convention WhatsApp / Slack use. */
function dayKey(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Human day label like ``10 Aug 2026`` (day-first, 2-digit day). Composed by
 *  hand rather than via ``toLocaleDateString`` so the order stays fixed across
 *  locales — matching the date pill in the discovery-client chat. */
function formatDay(ts: number): string {
  const d = new Date(ts);
  const dd = String(d.getDate()).padStart(2, "0");
  const mmm = d.toLocaleString(undefined, { month: "short" });
  return `${dd} ${mmm} ${d.getFullYear()}`;
}

/** Centred date pill between messages that cross a day boundary — the inline
 *  half of the WhatsApp-style sticky date. The floating pill at the top of the
 *  list (see the return block) mirrors whichever day sits at the viewport top
 *  and hands off seamlessly to this inline pill as it scrolls under it — the
 *  two share styling so the swap is invisible. */
function DateSeparator({ ts }: { ts: number }) {
  return (
    <div className="flex justify-center select-none" role="separator" aria-label={formatDay(ts)}>
      <span className="rounded-full bg-space-20 px-2.5 py-1 text-[11px] font-medium text-space-50 shadow-sm">
        {formatDay(ts)}
      </span>
    </div>
  );
}

/**
 * Footer rendered as the last virtual row. Houses the streaming
 * indicator, evaluator block, blocking widgets, and any queued
 * messages. Kept inside Virtuoso (rather than rendered as a sibling)
 * so ``followOutput`` keeps the bottom of *this* element pinned —
 * otherwise the queued bubbles would slide below the viewport.
 *
 * State is read from the store directly so the component can be
 * passed to Virtuoso's ``components.Footer`` slot as a stable
 * module-level reference — same reasoning as ``ListHeader``. With an
 * inline ``() => <ListFooter … />`` wrapper, Virtuoso treats each
 * parent render as a new Footer type and unmount/remounts it. That
 * blows away the local ``selections`` state inside
 * ``UserQuestionWidget``, so the moment a near-bottom scroll triggers
 * any parent re-render (e.g. ``setShowScrollDown`` flipping on
 * ``atBottomStateChange``) the user's in-progress radio answers
 * vanish.
 */
function ListFooter() {
  const isAgentRunning = useChatStore((s) => s.isAgentRunning);
  const evaluatorState = useChatStore((s) => s.evaluatorState);
  const streamingText = useChatStore((s) => s.streamingText);
  const thinkingText = useChatStore((s) => s.thinkingText);
  // Sub-agent runs keep the "Working" indicator visible even when a collapsed
  // "Thought" panel is present — otherwise the count/activity would be hidden
  // behind lingering thinking text and the turn would still look stalled.
  const hasRunningSubagents = useRunningSubagents().length > 0;
  const pendingQuestions = useChatStore((s) => s.pendingQuestions);
  const pendingApproval = useChatStore((s) => s.pendingApproval);
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const queuedMessages = useChatStore(
    (s) => (activeConversationId && s.queuedMessages[activeConversationId]) || EMPTY_QUEUE,
  );

  // Mirror the ``max-w-3xl mx-auto px-4`` column used by every virtualised
  // row so the streaming-typing dots, evaluator block, approval banners,
  // and queued bubbles align with the message column instead of hugging
  // the scroller's left edge.
  return (
    <div className="max-w-3xl mx-auto px-4 space-y-3 pb-2">
      {evaluatorState ? <EvaluatorBlock /> : null}
      {pendingQuestions ? <UserQuestionWidget /> : null}
      {pendingApproval ? <ApprovalBanner /> : null}
      {thinkingText && !evaluatorState ? <ThinkingPanel /> : null}
      {isAgentRunning && !evaluatorState ? (
        streamingText ? (
          <MessageErrorBoundary messageId="__streaming__">
            <StreamingBubble text={streamingText} />
          </MessageErrorBoundary>
        ) : thinkingText && !hasRunningSubagents ? null : (
          <StreamingIndicator />
        )
      ) : null}
      {queuedMessages.map((qm, i) => (
        // Keep the ``queued-`` id prefix — MessageBubble keys its "queued"
        // styling off it. React key uses the stable client id.
        <MessageErrorBoundary key={qm.id} messageId={`queued-${i}`}>
          <MessageBubble
            message={{
              id: `queued-${i}`,
              role: "user",
              content_json: JSON.stringify([{ type: "text", text: qm.text }]),
              created_at: new Date().toISOString(),
            }}
          />
        </MessageErrorBoundary>
      ))}
    </div>
  );
}

/** Passive end-of-history marker shown above the first message once the
 *  entire conversation is loaded (no more older pages in the backend, i.e.
 *  ``hasMoreOlder`` is false). Quieter than the jove-accented Unread divider
 *  — it reassures the user that scrolling up has bottomed out rather than
 *  inviting an action. */
function StartOfChatMarker() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-4">
      <div
        className="flex items-center gap-3 select-none"
        role="separator"
        aria-label="Start of conversation"
      >
        <div className="flex-1 h-px bg-space-30" />
        <span className="text-[11px] font-medium text-space-40 whitespace-nowrap">
          You've reached the top of this chat
        </span>
        <div className="flex-1 h-px bg-space-30" />
      </div>
    </div>
  );
}

/** Shared guard so both the IO callback and the manual click path see
 *  the same up-to-date paging state. Reading from ``getState()`` (rather
 *  than closing over selector values) means a stale closure can't fire a
 *  spurious second fetch while the previous one is still in flight. */
function triggerLoadOlder() {
  const s = useChatStore.getState();
  if (s.hasMoreOlder && !s.loadingOlder) void s.loadOlderMessages();
}

/**
 * Header rendered above the first virtual row. Shows a shimmer placeholder
 * while an upward-paging fetch is in flight so the user gets immediate
 * feedback that "load older" was registered.
 *
 * Detection is via ``IntersectionObserver`` on a zero-height sentinel:
 * the moment the sentinel intersects the viewport — whether because the
 * user scrolled to it or because the loaded window already fits in view
 * — ``loadOlderMessages`` fires. This sidesteps Virtuoso's
 * ``startReached`` needing a fresh scroll event, which is exactly the
 * failure mode that leaves the indicator stuck in short chats.
 *
 * The IO callback fires only on intersection *transitions*, so a
 * successful load that leaves the sentinel still in view won't cascade —
 * the user has to scroll the sentinel out and back in (or click the
 * label) for the next page.
 *
 * State is read directly from the store rather than via props so the
 * component can be passed to Virtuoso's ``components.Header`` slot as a
 * stable module-level reference. With inline ``() => <ListHeader … />``
 * wrappers, Virtuoso would treat each parent render as a new Header
 * type, unmount/remount the sentinel, and re-fire the IO against a
 * still-intersecting target — cascading through every loadable page on
 * any unrelated re-render.
 */
function ListHeader() {
  const loadingOlder = useChatStore((s) => s.loadingOlder);
  const hasMoreOlder = useChatStore((s) => s.hasMoreOlder);
  // Owned by ``MessageList``'s scroll handler: true only when the user has
  // scrolled to the top of a *scrollable* conversation with no more history
  // to load. Gating on it keeps the marker off fresh/short chats that don't
  // even have a scrollbar.
  const historyTopReached = useChatStore((s) => s.historyTopReached);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) triggerLoadOlder();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Whole conversation is loaded — there is nothing more to page in. Show the
  // passive "top of chat" marker instead of the load-older affordance, but
  // only once the user has actually scrolled up to the top of a scrollable
  // chat (``historyTopReached``) — never by default on a short/fresh chat.
  if (!loadingOlder && !hasMoreOlder) {
    return historyTopReached ? <StartOfChatMarker /> : null;
  }
  return (
    <div className="max-w-3xl mx-auto px-4 py-3 space-y-2">
      <div ref={sentinelRef} aria-hidden="true" />
      {loadingOlder ? (
        <>
          <div className="h-3 w-3/4 shimmer rounded" />
          <div className="h-3 w-2/3 shimmer rounded" />
          <div className="h-3 w-1/2 shimmer rounded" />
        </>
      ) : (
        <button
          type="button"
          onClick={triggerLoadOlder}
          className="w-full text-xs text-center text-space-40 py-1 rounded
                     hover:text-jove hover:bg-jove-10 transition-colors cursor-pointer"
        >
          Load older messages…
        </button>
      )}
    </div>
  );
}

export function MessageList() {
  const messages = useChatStore((s) => s.messages);
  const unreadCutoff = useChatStore((s) => s.unreadCutoff);
  const toolCalls = useChatStore((s) => s.toolCalls);
  const isAgentRunning = useChatStore((s) => s.isAgentRunning);
  const lastStreamedMessageId = useChatStore((s) => s.lastStreamedMessageId);
  const genuiHtml = useChatStore((s) => s.genuiHtml);
  const artifacts = useChatStore((s) => s.artifacts);
  const sendMessage = useChatStore((s) => s.sendMessage);
  const sendGenuiAction = useChatStore((s) => s.sendGenuiAction);
  const saveWidgetAsArtifact = useChatStore((s) => s.saveWidgetAsArtifact);
  const hasMoreOlder = useChatStore((s) => s.hasMoreOlder);
  const loadingOlder = useChatStore((s) => s.loadingOlder);
  const loadOlderMessages = useChatStore((s) => s.loadOlderMessages);
  const hasMoreNewer = useChatStore((s) => s.hasMoreNewer);
  const loadingNewer = useChatStore((s) => s.loadingNewer);
  const loadNewerMessages = useChatStore((s) => s.loadNewerMessages);
  // The message to flash after a search "jump to match"; null otherwise.
  const highlightedMessageId = useChatStore((s) => s.highlightedMessageId);
  const setAtBottom = useChatStore((s) => s.setAtBottom);
  const pruneOldMessagesIfSafe = useChatStore((s) => s.pruneOldMessagesIfSafe);
  const scrollToBottomTick = useChatStore((s) => s._scrollToBottomTick);

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  // Direct handle to Virtuoso's underlying scroller div. Needed because
  // ``scrollToIndex`` only targets virtualised items — the typing dots,
  // queued bubbles, and evaluator banners live in the Footer component,
  // which sits *below* the last data item. ``scrollTo(scrollHeight)``
  // covers both the items and the footer.
  const scrollerRef = useRef<HTMLElement | null>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  // Floating date pill (WhatsApp-style): the day at the top of the viewport,
  // mirrored from the topmost mounted row's ``data-day-ts``. Hands off to the
  // inline ``DateSeparator`` pills as they scroll under it. ``stickyRafRef``
  // coalesces the scroll-driven layout read to one per frame.
  const [stickyDayTs, setStickyDayTs] = useState<number | null>(null);
  const stickyRafRef = useRef(0);
  // Stick-to-bottom: ``following`` is true while the view should track new
  // content; it's released when the user scrolls up past STICK_THRESHOLD.
  // ``stickHeightRef`` lets the run-time pin fire only on real growth.
  const followingRef = useRef(true);
  const stickHeightRef = useRef(-1);
  // Last observed ``scrollTop`` — lets ``handleUserScroll`` tell a real upward
  // gesture (scrollTop dropping) apart from content growth / our own bottom-pin
  // (scrollTop rising or flat), so only the former ever releases stick.
  const lastScrollTopRef = useRef(0);
  // Until this timestamp, scroll events come from our own (smooth) scroll, not
  // the user, so the scroll handler ignores them — otherwise the glide's
  // mid-flight positions would flash the CTA before landing at the bottom.
  const programmaticUntilRef = useRef(0);

  // ─── Open-scroll settle ───────────────────────────────────────────────
  // While ``true`` we re-assert the open target (see ``openTarget`` near the
  // return) on every list-height change, so asynchronously-resizing widgets
  // can't strand the view short of where it should land. Flipped off the
  // moment a real user scroll, a send, or an agent run takes over the
  // scroll — see ``stopSettling`` and the disengage effects below.
  const settlingRef = useRef(true);
  // Mount-time value of the send tick. Comparing against it lets the
  // send-scroll effect tell a genuine post-mount send apart from the effect
  // simply running on mount (where ``scrollToBottomTick`` equals this).
  const mountTick = useRef(scrollToBottomTick);

  // Lookup of tool_result rows by tool_use_id, for historical (reloaded) views.
  // Shared with the sub-agent drill-in modal — see ``transcriptShared``.
  const toolResultMap = useMemo(() => buildToolResultMap(messages), [messages]);

  // Resolve a tool_use message into a ToolCallState (live call wins over the row).
  const resolveToolCall = useCallback(
    (msg: Message): ToolCallState | null =>
      resolveToolCallShared(msg, toolResultMap, toolCalls),
    [toolCalls, toolResultMap],
  );

  // Group consecutive tool_use messages together
  const renderItems = useMemo<RenderItem[]>(() => {
    const items: RenderItem[] = [];
    let currentGroup: ToolCallState[] = [];
    let groupStartId = "";
    let groupStartTs = 0; // created_at (ms) of the group's first tool_use — its day

    // Locate the first message past the cutoff that should sit *under*
    // the Unread divider. We deliberately skip user messages (a queued
    // echo from the user themselves isn't "unread") and tool_result
    // rows (those are folded into the preceding tool_use group). The
    // divider is then injected immediately before the render item that
    // wraps this anchor message.
    let unreadAnchorId: string | null = null;
    if (unreadCutoff) {
      const cutoffMs = parseUTC(unreadCutoff).getTime();
      for (const m of messages) {
        // ``model_switch`` / ``env_switch`` markers and ``thinking`` disclosures
        // are system content, not unread answers — never anchor the divider on one.
        if (
          m.role === "user"
          || m.role === "tool_result"
          || m.role === "model_switch"
          || m.role === "env_switch"
          || m.role === "thinking"
        ) continue;
        if (parseUTC(m.created_at).getTime() > cutoffMs) {
          unreadAnchorId = m.id;
          break;
        }
      }
    }
    let dividerInserted = false;
    const maybeInsertDivider = (msgId: string) => {
      if (!dividerInserted && unreadAnchorId && msgId === unreadAnchorId) {
        // Flush any in-progress tool group first so the divider doesn't
        // land *inside* a collapsible block.
        if (currentGroup.length > 0) {
          items.push({ kind: "toolGroup", id: groupStartId, ts: groupStartTs, toolCalls: [...currentGroup] });
          currentGroup = [];
          groupStartId = "";
        }
        items.push({ kind: "unreadDivider", id: "unread-divider" });
        dividerInserted = true;
      }
    };

    const flushGroup = () => {
      if (currentGroup.length > 0) {
        items.push({ kind: "toolGroup", id: groupStartId, ts: groupStartTs, toolCalls: [...currentGroup] });
        currentGroup = [];
        groupStartId = "";
      }
    };

    for (const msg of messages) {
      if (msg.role === "tool_result") continue;

      if (msg.role === "tool_use" && msg.tool_use_id) {
        maybeInsertDivider(msg.id);
        const tc = resolveToolCall(msg);
        if (tc) {
          if (currentGroup.length === 0) {
            groupStartId = msg.id;
            groupStartTs = parseUTC(msg.created_at).getTime();
          }
          currentGroup.push(tc);
        }
        continue;
      }

      maybeInsertDivider(msg.id);

      // Artifact message — render as a file card
      if (msg.role === "artifact") {
        flushGroup();
        try {
          const blocks = JSON.parse(msg.content_json);
          const block = blocks.find((b: { type: string }) => b.type === "artifact");
          if (block) {
            items.push({
              kind: "artifact",
              id: msg.id,
              ts: parseUTC(msg.created_at).getTime(),
              artifactId: block.artifact_id,
              filename: block.filename,
              fileType: block.file_type,
            });
            continue;
          }
        } catch { /* fall through to message */ }
      }

      // Generative UI message — render as interactive iframe
      if (msg.role === "genui") {
        flushGroup();
        try {
          const blocks = JSON.parse(msg.content_json);
          const block = blocks.find((b: { type: string }) => b.type === "genui");
          if (block) {
            // The genui_id is the message id: the server persists the genui
            // block without a genui_id field (see SessionManager._emit_genui),
            // so a *reloaded* widget's block has none — fall back to msg.id so
            // the widget keeps a stable identity (height cache, Save CTA).
            const genuiId = block.genui_id || msg.id;
            // Use live HTML from genuiHtml map (auto-refreshed) or fall back to stored
            const liveHtml = genuiHtml.get(genuiId);
            items.push({
              kind: "genui",
              id: msg.id,
              ts: parseUTC(msg.created_at).getTime(),
              genuiId,
              html: liveHtml || block.html,
              title: block.title || "",
              filePath: block.file_path || "",
            });
            continue;
          }
        } catch { /* fall through to message */ }
      }

      // Non-tool message — flush any accumulated group
      flushGroup();
      items.push({ kind: "message", msg });
    }

    // Flush trailing group
    flushGroup();

    // ─── Day separators (WhatsApp-style) ──────────────────────────────────
    // Walk the finished list and splice a centred date pill before the first
    // row of each new calendar day. Kept as a second pass — rather than woven
    // into the grouping above — so the day logic stays independent of tool
    // folding and unread-divider placement. Every dated row is also tagged with
    // ``data-day-ts`` in ``itemContent`` so the floating sticky pill can read
    // whichever day currently sits at the viewport top.
    const withDates: RenderItem[] = [];
    let lastDayKey: string | null = null;
    for (const it of items) {
      const ts =
        it.kind === "message"
          ? parseUTC(it.msg.created_at).getTime()
          : it.kind === "unreadDivider"
            ? null
            : it.ts;
      if (ts !== null && Number.isFinite(ts)) {
        const key = dayKey(ts);
        if (key !== lastDayKey) {
          withDates.push({ kind: "dateSeparator", id: `date:${key}`, ts });
          lastDayKey = key;
        }
      }
      withDates.push(it);
    }
    return withDates;
  }, [messages, resolveToolCall, genuiHtml, unreadCutoff]);

  // Basenames of files already registered as artifacts — lets a widget's
  // Save CTA flip to "Saved" the moment its file is pinned (live or on
  // reload). Keyed on path basename to match the agent's tool_use paths,
  // falling back to the display filename when an artifact streamed without
  // a path. Mirrors the de-dup the Files panel does.
  const savedWidgetNames = useMemo(
    () => new Set(artifacts.map((a) => (a.file_path ? basenameOf(a.file_path) : a.filename))),
    [artifacts],
  );

  // Trigger an opportunistic prune whenever the loaded window grows past
  // the cap. Cheaply short-circuited inside the store when the user is
  // scrolled up or an agent run is active.
  useEffect(() => {
    pruneOldMessagesIfSafe();
  }, [messages.length, isAgentRunning, pruneOldMessagesIfSafe]);

  // Force-scroll to the bottom whenever the user submits a new message.
  // ``followOutput`` only auto-follows when the user is already at the
  // bottom, which leaves them stranded if they typed while scrolled up.
  // Reading from a store-driven tick (rather than reacting to messages
  // length) keeps WS-driven appends like ``assistant_text`` out of the
  // trigger — only user-originated sends snap the view.
  //
  // The scroll is deferred via ``requestAnimationFrame`` so Virtuoso has
  // a chance to mount and measure the just-appended user bubble before
  // we ask it to scroll past it. Without the deferral the call lands at
  // the *previous* last item, leaving the new bubble below the viewport
  // — which then makes ``followOutput``'s ``isAtBottom`` check return
  // false and breaks auto-scroll for the streamed reply.
  //
  // ``behavior: "auto"`` (instant) is intentional: a smooth scroll keeps
  // ``isAtBottom`` momentarily false for the duration of the animation,
  // which would race the first ``assistant_text`` token and again break
  // ``followOutput``. Snapping flips the state to true immediately so
  // streaming pins as expected.
  //
  // The guard skips the run on mount (tick still equals its mount value)
  // so opening a chat doesn't fight ``initialTopMostItemIndex`` or the
  // open-scroll settle — only a *genuine* post-mount send snaps the view.
  useEffect(() => {
    if (scrollToBottomTick === mountTick.current) return;
    // A send owns the scroll now; stand the open-scroll settle down so the
    // two don't fight over where the view sits.
    settlingRef.current = false;
    // Re-engage stick so the streamed reply (and the growing footer) tracks
    // the bottom even though sending pushed the prior view off it.
    followingRef.current = true;
    stickHeightRef.current = -1;
    setShowScrollDown(false);
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      // Second frame: ResizeObserver fires between the two rAFs so
      // Virtuoso has measured the just-appended user bubble *and* the
      // footer (typing dots, queued bubbles) by the time we land the
      // scroll.
      raf2 = requestAnimationFrame(() => {
        // Use the underlying scroller — ``scrollToIndex`` only addresses
        // virtualised items and would stop at the user's bubble, leaving
        // the typing indicator clipped below. ``scrollTop = scrollHeight``
        // covers the items *plus* the Footer slot.
        const scroller = scrollerRef.current;
        if (scroller) {
          scroller.scrollTop = scroller.scrollHeight;
        } else {
          virtuosoRef.current?.scrollToIndex({
            index: renderItems.length - 1,
            align: "end",
            behavior: "auto",
          });
        }
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
    // ``renderItems.length`` is deliberately not a dep — we want to scroll
    // to the bottom *as it is at this moment*, then let ``followOutput``
    // take over for the streamed tokens that follow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToBottomTick]);

  const handleStartReached = useCallback(() => {
    if (hasMoreOlder && !loadingOlder) {
      void loadOlderMessages();
    }
  }, [hasMoreOlder, loadingOlder, loadOlderMessages]);

  // Page *down* when the bottom of the loaded window is reached. Only does
  // anything after a search jump seeded the window mid-conversation with newer
  // messages still unloaded below (``hasMoreNewer``); a normal open already
  // holds the newest message, so this is a no-op and never interferes with the
  // ordinary bottom-anchored / streaming scroll behaviour.
  const handleEndReached = useCallback(() => {
    if (hasMoreNewer && !loadingNewer) {
      void loadNewerMessages();
    }
  }, [hasMoreNewer, loadingNewer, loadNewerMessages]);

  // Store-level bottom state only. The CTA's visibility is owned by the
  // scroll handler below, which (unlike Virtuoso's atBottom) doesn't trip on
  // the footer growing taller while we pin a streaming reply.
  const handleAtBottomChange = useCallback((atBottom: boolean) => {
    setAtBottom(atBottom);
  }, [setAtBottom]);

  // Decide whether the "you've reached the top of this chat" marker should
  // show. Three conditions, all required: the conversation is meaningfully
  // scrollable (more than the hysteresis band of overflow — which also
  // guarantees the hide threshold is reachable), there is no older history
  // left to page in, and the user has scrolled up to the top. Store state is
  // read via getState() so the scroll listener can stay a stable [] callback.
  const updateTopReached = useCallback(() => {
    const sc = scrollerRef.current;
    if (!sc) return;
    const store = useChatStore.getState();
    const scrollable = sc.scrollHeight - sc.clientHeight > TOP_REACH_HIDE;
    if (!scrollable || store.hasMoreOlder) {
      store.setHistoryTopReached(false);
      return;
    }
    const top = sc.scrollTop;
    // Hysteresis (see TOP_REACH_*): show below SHOW, hide only above HIDE, hold
    // in between — so the layout shift from revealing the marker can't bounce
    // scrollTop back across a single threshold and flicker.
    if (!store.historyTopReached && top <= TOP_REACH_SHOW) store.setHistoryTopReached(true);
    else if (store.historyTopReached && top > TOP_REACH_HIDE) store.setHistoryTopReached(false);
  }, []);

  // Read the day at the top of the viewport and drive the floating date pill.
  // Scans the mounted rows' ``data-day-ts`` and picks the last one whose top
  // has crossed the pill line — that row owns the top of the view. Works under
  // virtualisation because we only need the *visible* rows, which are always
  // mounted; a far-scrolled day never leaves us blind since every row carries
  // its own day. rAF-coalesced so a burst of scroll events costs one layout
  // read. Depends only on the (stable) scroller ref, so it stays a ``[]``
  // callback safe to attach once.
  const updateStickyDay = useCallback(() => {
    if (stickyRafRef.current) return;
    stickyRafRef.current = requestAnimationFrame(() => {
      stickyRafRef.current = 0;
      const sc = scrollerRef.current;
      if (!sc) return;
      const line = sc.getBoundingClientRect().top + 12;
      let bestTs: number | null = null;
      let bestTop = -Infinity;
      let firstTs: number | null = null;
      sc.querySelectorAll<HTMLElement>("[data-day-ts]").forEach((el) => {
        const ts = Number(el.dataset.dayTs);
        if (!Number.isFinite(ts)) return;
        if (firstTs === null) firstTs = ts;
        const top = el.getBoundingClientRect().top;
        if (top <= line && top > bestTop) {
          bestTop = top;
          bestTs = ts;
        }
      });
      // Before any row has crossed the line (scrolled hard against the top of
      // the chat) fall back to the earliest mounted day so the pill still reads.
      setStickyDayTs(bestTs ?? firstTs);
    });
  }, []);

  // Engage/release stick from genuine user scrolling.
  //
  // The subtlety this guards against: while the agent streams, appended content
  // and our own bottom-pin both fire ``scroll`` events where the view is
  // *momentarily* far from the bottom — a tall tool-call block lands a frame
  // before the re-pin catches it, so the pin's own scroll event reads a
  // scrollHeight that already grew past where we pinned. The old
  // ``following = distance <= threshold`` test read that as the user scrolling
  // up, released stick, and (because the pin loop gates on ``followingRef``)
  // stopped following — the view fell behind and the CTA appeared though the
  // user never touched the scrollbar.
  //
  // So we only *release* on a real upward gesture: ``scrollTop`` actually
  // dropping past the buffer. Growth and downward pins move ``scrollTop`` up or
  // leave it flat, so they can never release. Re-engaging is lenient — any
  // scroll that lands back within the buffer re-pins and drops the CTA — so
  // returning to the bottom always resumes auto-follow.
  const handleUserScroll = useCallback(() => {
    const sc = scrollerRef.current;
    if (!sc) return;
    const top = sc.scrollTop;
    const prevTop = lastScrollTopRef.current;
    // Track position even for our own (programmatic) glide so the next *user*
    // delta is measured from where the glide left off — then ignore the event.
    lastScrollTopRef.current = top;
    if (performance.now() < programmaticUntilRef.current) return;
    const distance = sc.scrollHeight - sc.clientHeight - top;
    if (followingRef.current) {
      // ``- 1`` eats sub-pixel jitter so a still view can't self-release.
      if (top < prevTop - 1 && distance > STICK_THRESHOLD) {
        followingRef.current = false;
        setShowScrollDown(true);
      }
    } else if (distance <= STICK_THRESHOLD) {
      followingRef.current = true;
      setShowScrollDown(false);
    }
    updateTopReached();
  }, [updateTopReached]);

  const scrollToBottom = useCallback(() => {
    followingRef.current = true;
    stickHeightRef.current = -1;
    setShowScrollDown(false);
    // We're leaving the top; clear the marker now since the 700ms programmatic
    // window below suppresses the scroll handler that would otherwise do it.
    useChatStore.getState().setHistoryTopReached(false);
    programmaticUntilRef.current = performance.now() + 700;
    virtuosoRef.current?.scrollToIndex({
      index: renderItems.length - 1,
      align: "end",
      behavior: "smooth",
    });
  }, [renderItems.length]);

  const itemContent = useCallback((_index: number, item: RenderItem) => {
    // Representative local-day timestamp for this row. Tags the row with
    // ``data-day-ts`` so the floating sticky pill can read the day at the
    // viewport top by scanning the mounted rows (see ``updateStickyDay``).
    const dayTs =
      item.kind === "message"
        ? parseUTC(item.msg.created_at).getTime()
        : item.kind === "unreadDivider"
          ? undefined
          : item.ts;
    // Centred column matches the original ``max-w-3xl mx-auto`` look the
    // team had with the plain ``div``. Vertical spacing on each item
    // replaces ``space-y-3`` which doesn't apply across virtualised rows.
    const wrap = (node: React.ReactNode) => (
      <div className="max-w-3xl mx-auto px-4 py-1.5" data-day-ts={dayTs}>{node}</div>
    );
    if (item.kind === "dateSeparator") {
      return (
        <div className="max-w-3xl mx-auto px-4 py-1.5" data-day-ts={item.ts}>
          <DateSeparator ts={item.ts} />
        </div>
      );
    }
    if (item.kind === "unreadDivider") {
      // ``data-unread-divider`` lets the open-scroll settle re-anchor on the
      // divider's LIVE dom position (see ``pinOnce``) instead of a frozen data
      // index that goes stale the instant an older page prepends on open.
      return (
        <div className="max-w-3xl mx-auto px-4 py-1.5" data-unread-divider="true">
          <UnreadDivider />
        </div>
      );
    }
    if (item.kind === "toolGroup") {
      return wrap(<ToolCallGroup toolCalls={item.toolCalls} />);
    }
    if (item.kind === "artifact") {
      return wrap(<ArtifactCard artifactId={item.artifactId} filename={item.filename} fileType={item.fileType} />);
    }
    if (item.kind === "genui") {
      // A widget can be saved only when we know its backing file. Widgets
      // rendered before file_path was persisted (older conversations) omit
      // it — hide the CTA rather than offer a save that would 422.
      const canSave = item.filePath.length > 0;
      return wrap(
        <GenUIFrame
          genui_id={item.genuiId}
          html={item.html}
          title={item.title}
          onMessage={(text) => sendMessage(text)}
          onAction={(payload) => sendGenuiAction(payload)}
          onClose={() => {}}
          onSave={canSave ? async () => { await saveWidgetAsArtifact(item.genuiId); } : undefined}
          saved={canSave && savedWidgetNames.has(basenameOf(item.filePath))}
        />,
      );
    }
    return wrap(
      <MessageErrorBoundary messageId={item.msg.id}>
        <MessageBubble
          message={item.msg}
          entranceAnimation={item.msg.id !== lastStreamedMessageId}
          highlight={item.msg.id === highlightedMessageId}
        />
      </MessageErrorBoundary>,
    );
  }, [sendMessage, sendGenuiAction, saveWidgetAsArtifact, savedWidgetNames, lastStreamedMessageId, highlightedMessageId]);

  // Stable key per render item so Virtuoso can reuse rows when the
  // ordering shifts (tool groups expand, older messages prepend).
  const computeItemKey = useCallback((_index: number, item: RenderItem) => keyOfItem(item), []);

  // ─── Upward-pagination scroll anchoring (``firstItemIndex``) ──────────────
  // Without this prop, prepending an older page leaves Virtuoso's ``scrollTop``
  // untouched while the content *above* the viewport grows, so the messages the
  // user is reading lurch downward by the height of the just-loaded rows. The
  // jump is worst — and looks "random" — when a tall bubble sits in the
  // prepended page, because the displacement equals that bubble's full measured
  // height, resolved a beat *after* the prepend. ``firstItemIndex`` is
  // react-virtuoso's fix for exactly this: tell it how many items were
  // prepended and it pins the viewport to the same content across the growth.
  //
  // The delta must be expressed in *render items* (the ``data`` array), not raw
  // messages — grouping and tool_result folding mean the two counts differ. We
  // derive it purely, with no incremental mutation, so it stays correct under
  // React StrictMode's dev double-render: anchor on the key of the row that was
  // first at mount, and set ``firstItemIndex = BASE − (its current position)``.
  //   * Appends at the bottom (streamed tokens) don't move the anchor ⇒ index
  //     is unchanged ⇒ no spurious shift.
  //   * Each prepended row pushes the anchor down one ⇒ index drops by exactly
  //     the number of rows added above ⇒ Virtuoso holds the viewport still.
  // The anchor key only ever changes identity if the mount-time first row was a
  // tool group that later absorbs older tool_use rows (a boundary merge). That
  // folds rows *into* an existing row rather than adding rows, so on the rare
  // miss we re-baseline at the new head and hold the last index — Virtuoso's
  // own above-viewport size compensation covers the height change.
  const anchorKeyRef = useRef<string | null>(null);
  const anchorBaseRef = useRef(FIRST_ITEM_INDEX_BASE); // index when the anchor sits at position 0
  const lastFirstIndexRef = useRef(FIRST_ITEM_INDEX_BASE);
  const firstItemIndex = useMemo(() => {
    const keys = renderItems.map(keyOfItem);
    const head = keys[0] ?? null;
    if (anchorKeyRef.current === null) {
      // First non-empty render establishes the baseline.
      if (head !== null) anchorKeyRef.current = head;
      return lastFirstIndexRef.current;
    }
    const pos = keys.indexOf(anchorKeyRef.current);
    if (pos < 0) {
      // Boundary merge dissolved the anchor row — re-baseline at the current
      // head and keep the last index (height-only change, no net row prepend).
      if (head !== null) anchorKeyRef.current = head;
      anchorBaseRef.current = lastFirstIndexRef.current;
      return lastFirstIndexRef.current;
    }
    const value = anchorBaseRef.current - pos;
    lastFirstIndexRef.current = value;
    return value;
  }, [renderItems]);

  // Where the chat should land when it opens, snapshotted *at mount*. The
  // component unmounts and remounts on every conversation switch (ChatView
  // swaps in the skeleton while ``_historyLoaded`` is false, then renders
  // MessageList fresh), so a lazy ``useState`` initialiser recomputes this
  // per chat without manual keying, and a later re-render (streaming, tool
  // groups expanding) can't shift the anchor. Two deterministic cases, both
  // read straight off ``renderItems``:
  //
  //   * unread backlog present → the "Unread" divider, aligned to the *top*
  //     of the viewport so the user resumes exactly where they left off;
  //   * otherwise → the last row, aligned to the *bottom* (newest message).
  //   * search "jump to match" → the matched message, centred, so the user
  //     lands on it with context above and below (takes priority — it's an
  //     explicit destination the user picked).
  const [openTarget] = useState<FlatIndexLocationWithAlign>(() => {
    const targetId = useChatStore.getState().pendingScrollTargetId;
    if (targetId) {
      const targetIdx = renderItems.findIndex(
        (it) => it.kind === "message" && it.msg.id === targetId,
      );
      if (targetIdx >= 0) return { index: targetIdx, align: "center" };
    }
    const unreadIdx = renderItems.findIndex((it) => it.kind === "unreadDivider");
    return unreadIdx >= 0
      ? { index: unreadIdx, align: "start" }
      : { index: Math.max(0, renderItems.length - 1), align: "end" };
  });

  // ─── Open-scroll settle loop ──────────────────────────────────────────
  // The crux of the fix. Opening a chat must land on ``openTarget`` (the
  // bottom, or the unread divider) and *stay* there — but two forces fight
  // a single one-shot scroll, both confirmed in the browser:
  //
  //   1. Async widget growth. A GenUIFrame iframe reports its real height
  //      only after it loads (its bridge fires on ``load`` + a 500ms timer +
  //      a ResizeObserver), so rows above the landing spot grow a beat
  //      *after* the initial scroll and push the target out of view.
  //   2. Virtuoso re-asserts ``initialTopMostItemIndex`` over several frames
  //      using *estimated* sizes for the still-unmounted tail. With a tall
  //      widget in the list that estimate is far off, so the anchor lands
  //      mid-list and re-applies often enough to revert a single corrective
  //      pin (this is precisely the stranding the bug reported).
  //
  // A short, self-terminating loop beats both deterministically: re-assert
  // the target every tick until the list height holds steady for a few ticks
  // (layout settled) or a hard cap, whichever comes first. Pinning is cheap
  // and idempotent, "the bottom" is a *stable* target (correct no matter how
  // wrong the size estimate is), and out-ticking Virtuoso's transient
  // re-assertions makes *our* scroll the last word. ``setTimeout`` (not rAF)
  // drives it so it keeps ticking even when the tab isn't painting. The loop
  // never starts, or stops on its next tick, once the user scrolls or a
  // send/agent-run takes over — see ``settlingRef`` and the disengage hooks.
  const settleTimer = useRef(0);
  const loopRunning = useRef(false);
  const pinOnce = useCallback(() => {
    if (openTarget.align === "end") {
      const sc = scrollerRef.current;
      if (sc) {
        // ``scrollTop = scrollHeight`` rather than ``scrollToIndex(last)``:
        // a numeric index resolves through Virtuoso's bad cumulative-offset
        // estimate (a tall widget above it strands the view mid-list),
        // whereas the raw bottom is estimate-independent. It also covers the
        // Footer slot (typing dots / queued bubbles), which a data-row index
        // would clip. Only nudge when actually off the bottom so a settled
        // list isn't churned by redundant writes.
        if (sc.scrollHeight - sc.clientHeight - sc.scrollTop > 4) {
          sc.scrollTop = sc.scrollHeight;
        }
        return;
      }
    }
    // Unread case: align the divider to the top of the viewport. Prefer its
    // LIVE dom position over ``scrollToIndex(openTarget)`` — ``openTarget.index``
    // is a raw data index snapshotted at mount, and an older page prepending on
    // open (the IO sentinel auto-loads, confirmed in the browser) shifts the
    // divider down N rows, so re-scrolling to the stale index lands on a row N
    // positions *above* the divider and flickers the view toward the top. The
    // dom measurement re-reads the divider's real offset each tick, so a prepend
    // can't strand it. Fall back to the index scroll only when the divider isn't
    // currently mounted (virtualised far off-screen).
    const sc = scrollerRef.current;
    const divider = sc?.querySelector<HTMLElement>('[data-unread-divider="true"]');
    if (sc && divider) {
      const delta = divider.getBoundingClientRect().top - sc.getBoundingClientRect().top;
      if (Math.abs(delta) > 4) sc.scrollTop += delta;
      return;
    }
    virtuosoRef.current?.scrollToIndex(openTarget);
  }, [openTarget]);

  const runSettle = useCallback(() => {
    if (loopRunning.current || !settlingRef.current) return;
    loopRunning.current = true;
    let ticks = 0;
    let lastSh = -1;
    let lastTop = -1;
    let stable = 0;
    const STABLE_TICKS = 4; // ~200ms of *no* movement ⇒ truly settled on target
    const MAX_TICKS = 40;   // ~2s hard cap so the loop can never run away
    const TICK_MS = 50;
    const tick = () => {
      if (!settlingRef.current) { loopRunning.current = false; return; }
      pinOnce();
      const sc = scrollerRef.current;
      const sh = sc ? sc.scrollHeight : -1;
      const top = sc ? Math.round(sc.scrollTop) : -1;
      // Terminate only when neither the height *nor* the scroll position has
      // moved for a few ticks. Tracking ``scrollTop`` too — not just height —
      // is what makes this robust against Virtuoso re-asserting its own
      // estimate-based anchor *after* a pin: a revert moves ``scrollTop``,
      // which resets the counter, so the loop keeps correcting until our
      // target genuinely holds. (Height-only termination would quit the
      // instant growth paused, even mid-revert or mid-load.)
      if (sh === lastSh && top === lastTop) stable += 1;
      else { stable = 0; lastSh = sh; lastTop = top; }
      ticks += 1;
      if (stable >= STABLE_TICKS || ticks >= MAX_TICKS) {
        loopRunning.current = false;
        return;
      }
      settleTimer.current = window.setTimeout(tick, TICK_MS);
    };
    tick();
  }, [pinOnce]);

  // Start the loop on open. ``totalListHeightChanged`` also (re)starts it so
  // a late async widget resize — after the loop already settled — re-pins,
  // as long as the user hasn't taken over (the restart is a no-op while the
  // loop is still running or once settling has been disengaged).
  useEffect(() => {
    runSettle();
    return () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
      // Reset the guard so a later (re)start can run. Without this, React
      // StrictMode's dev double-invoke (setup → cleanup → setup, same refs)
      // leaves ``loopRunning`` stuck true after the first cleanup, so the
      // second setup — and every ``totalListHeightChanged`` after it — bails
      // and the loop never actually runs.
      loopRunning.current = false;
    };
    // Mount-only: ``openTarget`` is snapshotted, so ``runSettle`` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search "jump to match" bookkeeping, mount-only. ``openTarget`` has already
  // snapshotted the target id above, so consume it now — a later normal reopen
  // of this chat must not re-jump. Then let the flash play and clear the
  // highlight so the ring doesn't replay each time the row scrolls back into
  // view (Virtuoso remounts rows). Keyed off the highlight (not the consumed
  // target) so React StrictMode's double-invoke still re-arms the timer.
  useEffect(() => {
    const store = useChatStore.getState();
    if (store.pendingScrollTargetId !== null) store.consumeScrollTarget();
    if (store.highlightedMessageId === null) return;
    const t = window.setTimeout(() => useChatStore.getState().clearHighlightedMessage(), 2600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleListHeightChanged = useCallback(() => {
    runSettle();
    // A height change can flip scrollability or move the top out from under
    // the user (e.g. a late widget load) — re-check the top marker.
    updateTopReached();
  }, [runSettle, updateTopReached]);

  // Re-evaluate the top marker when "more to load" flips. Going false (history
  // exhausted) may need to *show* it if the user is already parked at the top
  // and no scroll event will fire; going true (a prune re-opened older
  // history) must hide it.
  useEffect(() => {
    if (hasMoreOlder) {
      useChatStore.getState().setHistoryTopReached(false);
      return;
    }
    const raf = requestAnimationFrame(updateTopReached);
    return () => cancelAnimationFrame(raf);
  }, [hasMoreOlder, updateTopReached]);

  // Disengage the moment the user scrolls under their own power.
  // ``wheel``/``touchmove`` are unambiguous user gestures — neither is
  // dispatched by our programmatic ``scrollToIndex``/``scrollTop`` writes,
  // so re-pinning can never feed back into a disengage.
  const stopSettling = useCallback(() => { settlingRef.current = false; }, []);
  const bindScroller = useCallback((ref: HTMLElement | Window | null) => {
    const prev = scrollerRef.current;
    if (prev) {
      prev.removeEventListener("wheel", stopSettling);
      prev.removeEventListener("touchmove", stopSettling);
      prev.removeEventListener("scroll", handleUserScroll);
      prev.removeEventListener("scroll", updateStickyDay);
    }
    const el = ref instanceof HTMLElement ? ref : null;
    scrollerRef.current = el;
    if (el) {
      el.addEventListener("wheel", stopSettling, { passive: true });
      el.addEventListener("touchmove", stopSettling, { passive: true });
      el.addEventListener("scroll", handleUserScroll, { passive: true });
      el.addEventListener("scroll", updateStickyDay, { passive: true });
      updateStickyDay();
    }
  }, [stopSettling, handleUserScroll, updateStickyDay]);

  // Recompute the floating date whenever the rendered rows change — a new
  // message streams in, a day separator is spliced, or an older page prepends —
  // and cancel any in-flight frame on unmount / before the next recompute.
  // Resetting the ref to 0 after cancelling is load-bearing: it doubles as the
  // "frame pending" guard in ``updateStickyDay``, so a bare cancel would leave
  // the guard stuck truthy and silently block every later scan (StrictMode's
  // mount double-invoke hits this deterministically — the pill never appears).
  useEffect(() => {
    updateStickyDay();
    return () => {
      if (stickyRafRef.current) {
        cancelAnimationFrame(stickyRafRef.current);
        stickyRafRef.current = 0;
      }
    };
  }, [renderItems, updateStickyDay]);

  // A run that STARTS while the user is viewing hands the scroll to
  // ``followOutput``; stand the settle down so streamed-token growth doesn't
  // yank the view back to a now-stale open target. (A user send disengages
  // inside the send-scroll effect above.) Crucially this must ignore the
  // MOUNT state: a chat opened mid-run has ``isAgentRunning`` already true
  // (row-derived Working), and disengaging here killed the settle loop
  // before its first pin — stranding the open at Virtuoso's estimate-based
  // anchor (#92: "opens at random places, every time, on running chats").
  const settleRunSnapshotRef = useRef<boolean | null>(null);
  useEffect(() => {
    const prev = settleRunSnapshotRef.current;
    settleRunSnapshotRef.current = isAgentRunning;
    if (prev === null) return; // mount snapshot — the settle owns the open
    if (!prev && isAgentRunning) settlingRef.current = false;
  }, [isAgentRunning]);

  // Stick-to-bottom for the run. ``followOutput`` only reacts to item-count
  // changes, so it misses the footer's streaming draft growing taller. While
  // the run is active and the user is following, pin to the bottom on each
  // real height growth so streamed tokens stay on screen.
  useEffect(() => {
    if (!isAgentRunning) return;
    stickHeightRef.current = -1;
    let raf = 0;
    const tick = () => {
      const sc = scrollerRef.current;
      if (sc && followingRef.current && sc.scrollHeight !== stickHeightRef.current) {
        stickHeightRef.current = sc.scrollHeight;
        if (sc.scrollHeight - sc.clientHeight - sc.scrollTop > 4) {
          sc.scrollTop = sc.scrollHeight;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isAgentRunning]);

  // Completion re-pin. When a run ends, the stick-to-bottom rAF above stops and
  // Virtuoso re-asserts its mount-time ``initialTopMostItemIndex``. For a chat
  // that was *empty* at mount that anchor is ``{index:0, align:"end"}`` —
  // pinning the first message to the viewport bottom, i.e. snapping to the top
  // of the chat. (Chats that mount with history anchor on the last row, so they
  // stay put — which is why the jump only bit fresh chats, and only sometimes:
  // it's a race with ``followOutput``'s completion scroll.) If the user was
  // following the bottom, out-tick that re-assertion until the position holds,
  // so completion lands and stays at the bottom. Bails the moment the user
  // scrolls up (``followingRef`` flips in ``handleUserScroll``).
  const prevRunningRef = useRef(isAgentRunning);
  useEffect(() => {
    const wasRunning = prevRunningRef.current;
    prevRunningRef.current = isAgentRunning;
    if (!(wasRunning && !isAgentRunning) || !followingRef.current) return;
    let timer = 0;
    let ticks = 0;
    let lastTop = -1;
    let stable = 0;
    const step = () => {
      if (!followingRef.current) return; // user took over
      const sc = scrollerRef.current;
      if (sc && sc.scrollHeight - sc.clientHeight - sc.scrollTop > 4) {
        sc.scrollTop = sc.scrollHeight;
      }
      const top = sc ? Math.round(sc.scrollTop) : -1;
      if (top === lastTop) stable += 1;
      else { stable = 0; lastTop = top; }
      ticks += 1;
      if (stable >= 4 || ticks >= 20) return; // held for ~200ms, or ~1s cap
      timer = window.setTimeout(step, 50);
    };
    step();
    return () => { if (timer) clearTimeout(timer); };
  }, [isAgentRunning]);

  return (
    <div className="h-full relative">
      {/* Floating date pill (WhatsApp-style): shows the day at the top of the
          viewport and hands off to the inline ``DateSeparator`` pills as they
          scroll under it. ``pointer-events-none`` so it never eats a click on
          the top row; identical styling to the inline pill ⇒ seamless handoff. */}
      {stickyDayTs !== null && (
        <div className="pointer-events-none absolute top-1.5 left-1/2 z-10 -translate-x-1/2">
          <span className="rounded-full bg-space-20 px-2.5 py-1 text-[11px] font-medium text-space-50 shadow-sm">
            {formatDay(stickyDayTs)}
          </span>
        </div>
      )}
      <Virtuoso
        ref={virtuosoRef}
        scrollerRef={bindScroller}
        data={renderItems}
        // Anchors the viewport when older pages prepend so the content the user
        // is reading doesn't lurch — see the derivation above. Paired with
        // ``data``; the delta tracks render-item prepends, not raw messages.
        firstItemIndex={firstItemIndex}
        computeItemKey={computeItemKey}
        itemContent={itemContent}
        startReached={handleStartReached}
        endReached={handleEndReached}
        atBottomStateChange={handleAtBottomChange}
        initialTopMostItemIndex={openTarget}
        // Re-pin to ``openTarget`` while the layout is still settling so
        // asynchronously-resizing widgets can't strand the open scroll.
        // Self-terminating: stops firing once heights stabilise.
        totalListHeightChanged={handleListHeightChanged}
        // ``alignToBottom`` makes short conversations sit at the bottom
        // of the viewport (chat-app feel) instead of floating at the top.
        alignToBottom
        // ``followOutput`` keeps the bottom edge pinned while new
        // tokens stream in, but yields immediately the moment the
        // user scrolls up — matching the behaviour the team had with
        // the manual scrollIntoView effect.
        followOutput={(isAtBottom) => (!isAgentRunning && isAtBottom ? "smooth" : false)}
        // A small overscan keeps row mounts ahead of the viewport so
        // fast flicks feel fluid; bigger numbers raise mount cost.
        increaseViewportBy={{ top: 400, bottom: 400 }}
        // Fire ``startReached`` a little before the user hits the absolute
        // top so the next page is already in flight by the time they get
        // there. Default is 0 which makes the trigger feel sluggish in
        // chats where the loaded window barely overflows the viewport.
        atTopThreshold={300}
        components={{
          // ``ListHeader`` reads paging state from the store directly so
          // it can be passed as a stable reference — see the comment on
          // the component itself for why that matters with Virtuoso.
          Header: ListHeader,
          Footer: ListFooter,
        }}
        className="scrollbar-on-hover"
        // Always reserve the scrollbar gutter so expanding a tool/thought
        // block (which adds vertical overflow) doesn't make the scrollbar
        // appear and re-centre the `mx-auto` message column — the whole list
        // used to nudge left. `scrollbar-gutter: stable` would be cleaner but
        // needs Chromium 94+ (the native app is on 87); `overflow-y: scroll`
        // works everywhere and `.scrollbar-on-hover` keeps the thumb hidden
        // until hover, so the reserved gutter reads as empty space at rest.
        style={{ height: "100%", overflowY: "scroll" }}
      />

      {/* Scroll to bottom floater */}
      {showScrollDown && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10
                     w-9 h-9 rounded-full bg-surface border border-space-30 shadow-lg
                     flex items-center justify-center
                     hover:bg-jove-10 hover:border-jove-60 hover:shadow-xl
                     transition-all duration-200 animate-[fadeInUpCentered_150ms_ease-out]"
          title="Scroll to bottom"
        >
          <svg className="w-4 h-4 text-space-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </button>
      )}
    </div>
  );
}
